import CollabFeatureIcon from 'assets/icons/mono/CollabFeatureIcon'
import CompetitorTrackingIcon from 'assets/icons/mono/CompetitorTrackingIcon'
import SelfSocialAccountsIcon from 'assets/icons/mono/SelfSocialAccountsIcon'
import { ANALYTICS_EVENTS } from 'constants/analyticsEvents'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { analyticsLog } from 'services/analytics'
import { startTrialPlan } from 'services/api/membership'
import { selectAgencyCurrency } from 'slices/auth/reducer'
import { fetchMembershipThunk } from 'slices/membership/thunk'
import { AppDispatch, RootState } from 'store'
import { Plan } from 'types/membership'
import { formatAmount, getErrorMessage } from 'utils/helpers'

const SubscriptionPlanCard: React.FC<{ plan: Plan, isActive?: boolean, frequency: string, onProceedToCheckout?: (planId: string | number, frequency: string) => void, onCancelMembership?: () => void }> = ({ plan, isActive = false, frequency, onProceedToCheckout, onCancelMembership }) => {


    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [processing, setProcessing] = useState(false);
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const planCurrency = plan.frequencies?.[frequency]?.currency;
    const agencyCurrency = useSelector(selectAgencyCurrency);


    const featuresList = useSelector((state: RootState) => state.membershipState.subscription_plans.data?.features)

    const features = useMemo(() => {
        return (featuresList ?? [])?.filter((feature) => feature.is_visible).reduce((acc, feature) => {
            acc[feature.slug] = { ...feature, value: plan.features[feature.id]?.value };
            return acc;
        }, {} as Record<string, any>)
    }, [featuresList, plan])
    const membershipData = useSelector((state: RootState) => state.membershipState.membership.data)

    const activePlan = membershipData?.active_plan;
    const isTrialPlan = useMemo(() => {
        return !!(activePlan?.plan_frequency?.is_trial);
    }, [activePlan]);

    const isFreePlan = useMemo(() => {
        return plan.default;
    }, [plan]);

    const currency = useMemo(() => {
        return isFreePlan ? agencyCurrency : planCurrency;
    }, [planCurrency, agencyCurrency, isFreePlan]);


    const isPaidPlan = useMemo(() => {
        if (isFreePlan) return false;
        return !(plan.frequencies?.[frequency]?.is_trial);
    }, [plan]);

    const isActivePaidPlan = useMemo(() => {
        return !(activePlan?.plan_frequency?.is_trial) && !activePlan?.plan?.default;
    }, [activePlan]);

    const startTrial = async () => {
        if (!selectedAgencyId) return;
        try {
            setProcessing(true);
            analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_TRIAL_START, {
                plan: plan.name,
                frequency: frequency,
            })
            await startTrialPlan(selectedAgencyId, plan.id);
            analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_TRIAL_SUCCESS, {
                plan: plan.name,
                frequency: frequency,
            })
            await dispatch(fetchMembershipThunk({ force: true }));
            navigate('/subscription-plans/trial', { replace: true });
        } catch (error) {
            analyticsLog(ANALYTICS_EVENTS.SUBSCRIPTION_TRIAL_FAILED, {
                plan: plan.name,
                frequency: frequency,
                error: getErrorMessage(error)
            })
            toast.error(getErrorMessage(error));
            console.log(error);
        } finally {
            setProcessing(false);
        }
    }

    const downgradeToFreePlan = async () => {
        setProcessing(true);
        try {
            if (onCancelMembership) {
                await onCancelMembership();
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
            console.log(error);
        } finally {
            setProcessing(false);
        }
    }

    const proceedToCheckout = async () => {
        setProcessing(true);
        try {
            if (onProceedToCheckout) {
                await onProceedToCheckout(plan.id, frequency);
            }
        } catch (error) {
            toast.error(getErrorMessage(error));
            console.log(error);
        } finally {
            setProcessing(false);
        }
    }

    return (
        <div className={`pricing-card position-relative ${plan.featured ? 'recommended-plan' : ''} ${isActive ? 'current-plan' : ''} h-100`}>
            {
                plan.featured &&
                <>
                    <div className='corner-circles'>
                        <svg width="191" height="153" viewBox="0 0 191 153" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="188.5" cy="-35.5" r="188" stroke="white" strokeOpacity="0.2" strokeLinecap="round" strokeDasharray="4 4" />
                            <circle cx="188.5" cy="-35.5" r="137" stroke="white" strokeOpacity="0.2" strokeLinecap="round" strokeDasharray="4 4" />
                            <circle cx="188.5" cy="-35.5" r="86" stroke="white" strokeOpacity="0.2" strokeLinecap="round" strokeDasharray="4 4" />
                        </svg>
                    </div>
                    {
                        !isActive &&
                        <span className="recommended-badge">
                            RECOMMENDED
                        </span>
                    }

                </>
            }
            {
                isActive &&
                <div className="d-flex align-items-center justify-content-center w-100">
                    <span className='current-plan-badge'>CURRENT PLAN {isTrialPlan ? '  •  FREE TRIAL' : ''} </span>
                </div>

            }
            <h5 className="plan-title">{plan.name.toUpperCase()} PLAN</h5>
            <p className="plan-subtitle">{plan.description}</p>

            <div className="plan-price">
                <h3 className="plan-amount">{formatAmount(plan.frequencies?.[frequency]?.is_trial ? (plan.frequencies?.['monthly']?.pre_tax_amount ?? 0) : (plan.frequencies?.[frequency]?.pre_tax_amount ?? 0), currency?.code, currency?.symbol, currency?.locale)}</h3>
                <span className="plan-frequency">
                    /month
                </span>
            </div>
            {
                (processing) ?
                    <button className={`plan-action-button ${isActive ? 'active' : ''}`} disabled>Please wait...</button> :
                    (
                        activePlan?.has_consumed_trial ?
                            (<>
                                {
                                    isTrialPlan ?
                                        (<>
                                            {
                                                isActive ?
                                                    (<button className='plan-action-button' onClick={proceedToCheckout}>SUBSCRIBE AND KEEP {(plan?.name ?? '').toUpperCase()} PLAN</button>) :
                                                    (<>
                                                        {
                                                            isFreePlan ?
                                                                (<button className='plan-action-button' onClick={downgradeToFreePlan} disabled={!(activePlan?.auto_renew)}>DOWNGRADE TO FREE PLAN</button>) :
                                                                (<button className='plan-action-button' onClick={proceedToCheckout}>UPGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN</button>)
                                                        }
                                                    </>)
                                            }
                                        </>) :
                                        (<>
                                            {
                                                isActivePaidPlan ?
                                                    (<>
                                                        {
                                                            isActive ? (
                                                                (
                                                                    <button className='plan-action-button active' disabled>
                                                                        YOUR CURRENT PLAN
                                                                    </button>
                                                                )
                                                            ) : (
                                                                <>
                                                                    {
                                                                        isFreePlan ?
                                                                            (<button className='plan-action-button' onClick={downgradeToFreePlan} disabled={!(activePlan?.auto_renew)}>DOWNGRADE TO FREE PLAN</button>) :
                                                                            (<>
                                                                                {
                                                                                    plan.sort_order > activePlan?.plan?.sort_order ?
                                                                                        (<button className='plan-action-button' onClick={proceedToCheckout}>UPGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN</button>) :
                                                                                        (<button className='plan-action-button' onClick={proceedToCheckout}>DOWNGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN</button>)
                                                                                }
                                                                            </>)
                                                                    }
                                                                </>
                                                            )
                                                        }
                                                    </>) :
                                                    (<>
                                                        {
                                                            isActive ?
                                                                (<button className='plan-action-button active' disabled>YOUR CURRENT PLAN</button>) :
                                                                (<button className='plan-action-button' onClick={proceedToCheckout}>
                                                                    UPGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN
                                                                </button>)
                                                        }
                                                    </>)
                                            }
                                        </>)
                                }
                            </>) :
                            (<>
                                {
                                    isActive ?
                                        (<button className='plan-action-button active' disabled>YOUR CURRENT PLAN</button>) :
                                        (
                                            <>
                                                {
                                                    isFreePlan ? <button className='plan-action-button' onClick={downgradeToFreePlan} disabled={!(activePlan?.auto_renew)}>DOWNGRADE TO FREE PLAN</button> :
                                                        (
                                                            <>
                                                                {
                                                                    (isActivePaidPlan && activePlan) ?
                                                                        ((<>
                                                                            {
                                                                                plan.sort_order > activePlan?.plan?.sort_order ?
                                                                                    (<button className='plan-action-button' onClick={proceedToCheckout}>UPGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN</button>) :
                                                                                    (<button className='plan-action-button' onClick={proceedToCheckout}>DOWNGRADE TO {(plan?.name ?? '').toUpperCase()} PLAN</button>)
                                                                            }
                                                                        </>)) :
                                                                        (<button className='plan-action-button' onClick={startTrial}>
                                                                            USE FREE TRIAL FOR 7 DAYS
                                                                        </button>)
                                                                }
                                                            </>
                                                        )
                                                }

                                            </>)
                                }
                            </>)
                    )

            }
            <div className="features-box">
                {
                    features.commission_rule_percent &&
                    <div className="commission-box">
                        Marketplace Commission <strong>{features.commission_rule_percent.value ? features.commission_rule_percent.value + '%' : '-'}</strong>
                    </div>
                }

                <div className="plan-features">
                    {
                        features.collabs &&
                        <span className='plan-feature'>
                            <span className='feature-title'>
                                <CollabFeatureIcon />
                                Collaboration Tracking
                            </span>
                            <span className='feature-limit'><strong>{features.collabs.value ?? 0}</strong> Slots</span>
                        </span>
                    }
                    {
                        features.max_competitors &&
                        <span className='plan-feature'>
                            <span className='feature-title'>
                                <CompetitorTrackingIcon />
                                Competitor Tracking
                            </span>
                            <span className='feature-limit'><strong>{features?.max_competitors?.value ?? 0}</strong> Slots</span>
                        </span>
                    }
                    {
                        features.max_ig_accounts &&
                        <span className='plan-feature'>
                            <span className='feature-title'>
                                <SelfSocialAccountsIcon />
                                Self Social Accounts
                            </span>
                            <span className='feature-limit'><strong>{features.max_ig_accounts.value ?? 0}</strong> Account</span>
                        </span>
                    }
                </div>
            </div>
        </div>
    )
}

export default SubscriptionPlanCard